import React, { useEffect, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  LabelList,
  Legend,
  Line,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatCurrency } from 'utils/string/currency';

type DashboardData = {
  name: string;
  month: string;
  budgetTotal: number;
  realizedTotal: number;
  percentage: number;
};

type ChartMetadataProps = {
  key: string;
  legendLabel: string;
  color?: string;
  stacked?: boolean;
  currency?: boolean;
};

type SimpleChartProps = {
  height?: number;
  width?: number;
  data: DashboardData[];
  dataChart: ChartMetadataProps[];
};

export const SimpleChart = ({
  data,
  dataChart,
  height = 400,
  width = 1400,
}: SimpleChartProps) => {
  const [chartWidth, setChartWidth] = useState(width);
  const normalizedData = data.map((item) => ({
    ...item,
    budgetTotal: item.budgetTotal / 1000,
    realizedTotal: item.realizedTotal / 1000,
  }));

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    setChartWidth(windowWidth - 500);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [windowWidth]);

  const maxBarValue = normalizedData.reduce((biggestValue, monthItem) => {
    const maxActualValue = Math.max(
      Number(monthItem.budgetTotal),
      Number(monthItem.realizedTotal)
    );

    if (maxActualValue > biggestValue) {
      return maxActualValue;
    } else {
      return biggestValue;
    }
  }, Number.MIN_SAFE_INTEGER);

  const maxBarChartValue = () => {
    const multiplier = maxBarValue > 1000 ? 500 : 1;
    return Math.ceil(maxBarValue / multiplier) * multiplier;
  };

  const CustomTooltip: React.FC<any> = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            background: '#252a41',
            padding: 12,
            borderRadius: 10,
          }}
        >
          <p className="text-primary font-s-100 font-weight-semibold pb-s-150">
            {payload[0].payload.month}
          </p>
          <div className="d-flex justify-between align-items-center">
            <p className="text-neutral-variant-80 pr-s-200">Orçado:</p>
            <p className="text-neutral-variant-95 ml-auto">
              {formatCurrency(payload[0].payload.budgetTotal * 1000)}
            </p>
          </div>
          <div className="d-flex justify-between align-items-center">
            <p className="text-neutral-variant-80 pr-s-200">Realizado:</p>
            <p className="text-neutral-variant-95 ml-auto">
              {formatCurrency(payload[0].payload.realizedTotal * 1000)}
            </p>
          </div>
          <div className="d-flex justify-between align-items-center">
            <p className="text-neutral-variant-80 pr-s-200">% Alcançado:</p>
            <p className="text-neutral-variant-95 ml-auto">
              {payload[0].payload.percentage} %
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderColorfulLegendText = (value: string) => {
    return <span style={{ color: '#3f515a' }}>{value}</span>;
  };

  const barSize = normalizedData.length <= 3 ? 100 : 30;

  const formatValue = (value: { toLocaleString: () => any }) => {
    return value.toLocaleString().replace(',', '.');
  };

  const generateTicks = (maxValue: number) => {
    const tickIncrement = maxValue / 5;
    const tickMultipliers = [0, 1, 2, 3, 4, 5];
    return tickMultipliers.map((value) => value * tickIncrement);
  };

  return (
    <ComposedChart
      width={chartWidth}
      height={height}
      data={normalizedData}
      margin={{
        top: 0,
        right: 0,
        bottom: 0,
        left: 35,
      }}
    >
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="month" tick={{ fill: '#3f515a', fontSize: 18 }} />
      <YAxis
        type="number"
        yAxisId="barYAxis"
        tick={{ fill: '#3f515a', fontSize: 16 }}
        domain={[0, maxBarChartValue()]}
        ticks={generateTicks(maxBarChartValue())}
        tickFormatter={(value) => 'R$' + formatValue(value)}
        scale="linear"
        unit={'K'}
      />
      <YAxis
        type="number"
        yAxisId="lineYAxis"
        orientation="right"
        tick={{ fill: '#3f515a', fontSize: 18 }}
        domain={[0, 125]}
        ticks={generateTicks(125)}
        unit={'%'}
        scale="linear"
      >
        <Label offset={20} position="insideRight" angle={-90} fill="#3f515a" />
      </YAxis>
      <Tooltip content={<CustomTooltip />} />
      <Legend
        verticalAlign="bottom"
        align="left"
        height={50}
        iconSize={20}
        formatter={renderColorfulLegendText}
      />
      <Bar
        isAnimationActive={false}
        yAxisId="barYAxis"
        name={dataChart[0].legendLabel}
        dataKey={dataChart[0].key}
        barSize={barSize}
        fill={dataChart[0].color}
      />
      <Bar
        isAnimationActive={false}
        yAxisId="barYAxis"
        name={dataChart[1].legendLabel}
        dataKey={dataChart[1].key}
        barSize={barSize}
        fill={dataChart[1].color}
      />
      <Line
        isAnimationActive={false}
        yAxisId="lineYAxis"
        type="monotone"
        dataKey={dataChart[2].key}
        name={dataChart[2].legendLabel}
        stroke={dataChart[2].color}
      >
        <LabelList
          dataKey="percentage"
          position="insideTop"
          offset={-20}
          fill={dataChart[2].color}
          formatter={(value: number) => `${value} %`}
        />
      </Line>
    </ComposedChart>
  );
};
